/*
  Keeps the phone's alarms and the in-app ringer in step with the timetable:
  - whenever the lessons or the reminder choice change (and every time the app comes to the
    front), re-plan and re-book on the phone;
  - on the web (or the phone app while open), ring the full-screen overlay when a reminder is due.
*/
import { useGetTimetableQuery } from "app/services/timetable";
import { alarmSlice, MAIN_SELECTORS, remindersSlice } from "app/store";
import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { dueNow, planLessonAlarms } from "./lessonAlarms";
import { isNativeApp } from "./nativeApp";
import { applyPhoneAlarms } from "./nativeReminders";

export function useLessonAlarms(enabled: boolean) {
  const prefs = useSelector(MAIN_SELECTORS.reminders);
  const dispatch = useDispatch();
  const { data, refetch } = useGetTimetableQuery({ daysBack: 0, daysAhead: 14 }, { skip: !enabled, pollingInterval: 15 * 60 * 1000 });
  const rung = useRef<Set<string>>(new Set());

  const alarms = useMemo(() => (data ? planLessonAlarms(data.lessons, data.children, prefs) : []), [data, prefs]);

  // Book on the phone whenever the plan changes.
  useEffect(() => {
    if (!enabled || !data || !isNativeApp()) return;
    applyPhoneAlarms(alarms).then(() => dispatch(remindersSlice.actions.planned(new Date().toISOString()))).catch(() => undefined);
  }, [alarms, data, enabled, dispatch]);

  // Refresh the timetable when the app comes back to the front.
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => { if (document.visibilityState === "visible") void refetch(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [enabled, refetch]);

  // In-app ringer (the phone's own alarm covers the app being closed).
  useEffect(() => {
    if (!enabled || isNativeApp()) return;
    const check = () => {
      const due = dueNow(alarms);
      if (due && !rung.current.has(due.id)) { rung.current.add(due.id); dispatch(alarmSlice.actions.ring({ registerId: due.registerId, title: due.title, body: due.body })); }
    };
    check();
    const id = setInterval(check, 10000);
    return () => clearInterval(id);
  }, [alarms, enabled, dispatch]);

  return { alarms, lessons: data?.lessons ?? [], children: data?.children ?? [] };
}
